import { useCallback, useEffect, useRef } from 'react';
import { UseNavigateResult } from '@tanstack/react-router';
import { Authn, HandleCallbackProps } from '@frontend/auth';
import { AuthStorage, localStorageHelper, SsoTenant } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { hydraShellService } from '../services';
import { handlePostAuthRedirect } from '../services/route-utils';
import { ContentLoader } from './content-loader.tsx';
import { FormWrap } from './form-wrap';

interface Props<T extends string> {
  auth: Authn;
  navigate: UseNavigateResult<T>;
}

export const SsoSignInCallback = <T extends string>({ auth, navigate }: Props<T>) => {
  const { t } = useTranslation('SignIn');
  const hasStarted = useRef(false);

  const getAuthCallbackProps = (): HandleCallbackProps | undefined => {
    const ssoTenant = localStorageHelper.get<SsoTenant>(AuthStorage.sso_tenant);
    if (!!ssoTenant) {
      return {
        clientId: ssoTenant.clientId,
        redirectUri: new URL(ssoTenant.redirectUri),
      };
    }
    return undefined;
  };

  const handleCallback = useCallback(async () => {
    auth.changeAuthMethod('oidc');

    try {
      await auth.handleCallback(getAuthCallbackProps());
      const acceptLoginResponse = await hydraShellService.acceptOAuth2Flow();
      handlePostAuthRedirect(acceptLoginResponse.redirectTo || appConfig.DEFAULT_REDIRECT, navigate, {
        reason: 'acceptLoginResponse for oidc SSO',
      });
    } catch (e: any) {
      console.error(e);
      handlePostAuthRedirect(appConfig.DEFAULT_REDIRECT, navigate, {
        reason: 'error caught in SSO sign-in callback',
        error: e,
      });
    }
  }, [auth, navigate]);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;
    handleCallback();
  }, [handleCallback]);

  return (
    <FormWrap>
      <ContentLoader message={t('Logging in...')} show />
    </FormWrap>
  );
};
